/**
 * cae-personalisation.js — record what the STORE decided, not what the shopper did.
 *
 * WHY THIS EXISTS
 *   color-pref.js opens a product page on the colour we believe the shopper
 *   prefers. That is live on real traffic, and until now it left NO trace: the
 *   only thing analytics ever saw was a `variant_change` if the shopper moved
 *   away from it — which reads as "they chose Ranger Green" when the truth is
 *   "we opened on Coyote Tan and they rejected it".
 *
 *   It also cannot be reconstructed later. The colour service re-bakes every 6
 *   hours, so asking it tomorrow what it would have served yesterday gives the
 *   wrong answer. If the decision is not recorded AS IT HAPPENS, it is gone.
 *
 * WHAT IT EMITS (Tier A — everything rides in `detail`, no BigQuery change)
 *   personalisation_applied     once per page, after the picker settles
 *   personalisation_overridden  once, if the shopper then picks another colour
 *
 *   Together they answer the only question that matters about this system:
 *   when we choose a colour for someone, do they keep it?
 *
 * ISOLATION
 *   Reads color-pref.js purely through what it already publishes —
 *   `window.SE_colorPref`, the `SE_colorPrefReady` event, and
 *   `window.__colorPrefApplied` / `__sizePrefApplied`. Nothing upstream is
 *   edited, and this file failing can never affect the storefront.
 *
 *   Kill switch: window.CAE_PERSONALISATION_OFF = true
 *
 * THE SYNTHETIC-CLICK TRAP (measured, not assumed)
 *   color-pref.js applies its choice through commitClick(), which calls
 *   input.click() up to 6 times over ~4s. An override watcher that cannot tell
 *   that from a shopper would report OUR OWN action as a rejection, and the
 *   metric would read exactly backwards — the worst possible failure, because
 *   the number still looks plausible.
 *
 *   `change.isTrusted` DOES NOT WORK here, which is counter-intuitive enough to
 *   be worth recording: a scripted .click() produces an UNTRUSTED click, but the
 *   `change` that follows is dispatched by the browser as the radio's activation
 *   behaviour, so it arrives with isTrusted === TRUE. Measured in a harness
 *   against this exact file: scripted click isTrusted=false, resulting change
 *   isTrusted=true. A first version of this file gated on change.isTrusted and
 *   mislabelled a synthetic apply as a shopper override on the first try.
 *
 *   What actually separates them is that a human produces a POINTER OR KEY
 *   gesture inside the picker and a script never does. So we require a trusted
 *   pointerdown/mousedown/keydown/click on the picker within GESTURE_MS before
 *   a colour change counts as the shopper's. Scripted clicks generate no such
 *   gesture and are invisible to it.
 */
(function () {
  'use strict';
  if (window.CAE_PERSONALISATION_OFF) return;
  if (window.__caePersonalisationLoaded) return;   // never double-count a page
  window.__caePersonalisationLoaded = true;

  var SYSTEM = 'color_pref';
  // color-pref.js retries the picker 8 times with a growing backoff
  // (200,500,900,1400,2000,2700,3500,4400ms). Wait past the last one before
  // declaring what it applied, or we would report "nothing applied" for a
  // slow-rendering picker.
  var SETTLE_MS = 5200;
  var POLL_MS = 200;

  function emit(name, detail) {
    try {
      if (window.CAEDev && typeof window.CAEDev.emit === 'function') {
        window.CAEDev.emit(name, detail);
        return true;
      }
    } catch (e) {}
    return false;
  }

  /** CAEDev is loaded defer-first but self-waiting costs nothing and removes an
   *  ordering assumption from theme.liquid. */
  function emitWhenReady(name, detail, tries) {
    if (emit(name, detail)) return;
    if ((tries || 0) > 40) return;                 // ~8s then give up quietly
    setTimeout(function () { emitWhenReady(name, detail, (tries || 0) + 1); }, 200);
  }

  function handleOf() {
    var m = /\/products\/([^/?#]+)/.exec(location.pathname || '');
    return m ? m[1] : null;
  }

  function isPdp() {
    return !!document.querySelector('form[action*="/cart/add"]');
  }

  function pickerInputs() {
    return document.querySelectorAll(
      'variant-picker input[type="radio"], .variant-picker input[type="radio"]');
  }

  /** The radio carrying a given option value, so we can learn which RADIO GROUP
   *  is the colour one. Matching by value mirrors color-pref.js exactly. */
  function inputForValue(value) {
    if (!value) return null;
    var want = String(value).trim().toLowerCase();
    var inputs = pickerInputs();
    for (var i = 0; i < inputs.length; i++) {
      if (String(inputs[i].value).trim().toLowerCase() === want) return inputs[i];
    }
    return null;
  }

  /** Why the page was NOT personalised, in the same order color-pref.js decides
   *  it — so the reason we report is the reason it actually used. */
  function suppressedReason(pref) {
    if (!pref) return 'no_preference';
    if (!pref.colors || !pref.colors.length) return 'no_colours';
    if (/[?&]variant=/.test(location.search)) return 'variant_url';
    if (!isPdp()) return 'not_pdp';
    return null;
  }

  var reported = false;

  function report(pref) {
    if (reported) return;
    reported = true;

    var applied = window.__colorPrefApplied || null;
    var reason = suppressedReason(pref);

    emitWhenReady('personalisation_applied', {
      system: SYSTEM,
      surface: 'pdp',
      product_handle: handleOf(),
      // what we believed
      known: !!(pref && pref.colors && pref.colors.length),
      source: (pref && pref.source) || null,        // 'cache' | 'network'
      colours_offered: (pref && pref.colors) ? pref.colors.slice(0, 6) : [],
      // what actually happened on screen
      colour_applied: applied,
      size_applied: window.__sizePrefApplied || null,
      applied_ok: !!applied,
      // and why not, when not
      suppressed_reason: applied ? null : (reason || 'not_applied')
    });

    if (applied) watchOverride(applied);
  }

  /** A human's fingerprint: a TRUSTED pointer or key gesture landing inside the
   *  variant picker. color-pref.js's input.click() produces none of these (its
   *  own click event is untrusted, and it generates no pointer or key event at
   *  all), which is what makes this the honest discriminator — see the header. */
  var lastGestureAt = 0;
  function markGesture(e) {
    try {
      if (!e || !e.isTrusted) return;
      var t = e.target;
      if (!t || !t.closest) return;
      if (!t.closest('variant-picker, .variant-picker')) return;
      lastGestureAt = Date.now();
    } catch (err) {}
  }
  ['pointerdown', 'mousedown', 'keydown', 'click'].forEach(function (n) {
    document.addEventListener(n, markGesture, true);
  });
  var GESTURE_MS = 1200;   // a change follows its gesture in well under a second

  /** Did the shopper undo our choice? Only a change carrying a recent human
   *  gesture counts, and only inside the same radio group the colour was applied
   *  to — so a size change can never be mistaken for a colour rejection. */
  function watchOverride(appliedColour) {
    var appliedInput = inputForValue(appliedColour);
    var groupName = appliedInput ? appliedInput.name : null;
    var since = Date.now();
    var done = false;

    document.addEventListener('change', function (e) {
      if (done) return;
      var t = e && e.target;
      if (!t || t.type !== 'radio') return;
      // NOT e.isTrusted — the browser fires a TRUSTED change after a scripted
      // .click(), so it cannot separate us from a shopper. A real gesture can.
      if (Date.now() - lastGestureAt > GESTURE_MS) return;
      if (groupName && t.name !== groupName) return;
      var chosen = String(t.value || '').trim();
      if (!chosen) return;
      if (chosen.toLowerCase() === String(appliedColour).trim().toLowerCase()) return;

      done = true;
      emitWhenReady('personalisation_overridden', {
        system: SYSTEM,
        surface: 'pdp',
        product_handle: handleOf(),
        colour_applied: appliedColour,
        colour_chosen: chosen,
        option_name: groupName || null,
        ms_after_apply: Date.now() - since
      });
    }, true);
  }

  /** color-pref.js publishes BEFORE it touches the picker, so knowing the
   *  preference is not the same as having applied it — we wait for the applied
   *  marker (or for the retries to run out) before reporting either way. */
  function settleThenReport(pref) {
    var waited = 0;
    (function tick() {
      if (reported) return;
      if (window.__colorPrefApplied || waited >= SETTLE_MS) { report(pref); return; }
      waited += POLL_MS;
      setTimeout(tick, POLL_MS);
    })();
  }

  function begin(pref) { try { settleThenReport(pref); } catch (e) {} }

  // It may already have published before this file ran (cache hit is synchronous
  // from localStorage), so check first and listen second.
  if (window.SE_colorPref) {
    begin(window.SE_colorPref);
  } else {
    window.addEventListener('SE_colorPrefReady', function (e) {
      begin((e && e.detail) || window.SE_colorPref || null);
    });
    // No preference ever arrives for an unknown visitor. Report that ONCE on a
    // product page — "we had nothing for them" is a real answer, and without it
    // the denominator is invisible and every rate would be flattering.
    setTimeout(function () {
      if (!reported && !window.SE_colorPref && isPdp()) report(null);
    }, SETTLE_MS + 2000);
  }
})();
